import {
  ICart,
  ICartInfectiousAliquot,
  ICartInfectiousOrderItem,
  ISample,
} from "@/shared/types";

export type CartItemType = "sample" | "infectious";

export type CartProduct = ISample & {
  cartItemType: CartItemType;
  infectiousAliquotId?: number;
  rawCartItem?: ICartInfectiousOrderItem;
};

export const CART_TYPE_CONFLICT_TOOLTIP =
  "Your cart already contains samples of another type. Please complete your current purchase first.";

export const CART_TYPE_CONFLICT_TOOLTIP_STYLE = {
  color: "white",
  fontSize: "1.3rem",
  lineHeight: "1.8rem",
  maxWidth: "360px",
  zIndex: 10,
};

const getResponseData = (response: any) =>
  response?.data?.data ?? response?.data ?? response;

export const getInfectiousOrderItemsFromResponse = (
  response: any
): ICartInfectiousOrderItem[] => {
  const data = getResponseData(response);

  if (Array.isArray(data)) {
    return data;
  }

  if (Array.isArray(data?.items)) {
    return data.items;
  }

  if (Array.isArray(data?.infectiousOrderItems)) {
    return data.infectiousOrderItems;
  }

  if (Array.isArray(data?.infectious_order_items)) {
    return data.infectious_order_items;
  }

  return [];
};

export const mergeInfectiousOrderItemsIntoCart = (
  cart: ICart | null,
  response: any
): ICart | null => {
  if (!cart) {
    return cart;
  }

  return {
    ...cart,
    infectiousOrderItems: getInfectiousOrderItemsFromResponse(response),
  };
};

export const getCartInfectiousOrderItems = (
  cart?: ICart | null
): ICartInfectiousOrderItem[] => {
  const sources = [
    cart?.infectiousOrderItems,
    cart?.infectious_order_items,
    cart?.infectiousItems,
  ];

  return sources.find((items) => Array.isArray(items) && items.length) ?? [];
};

const getOrderItemAliquot = (
  item: ICartInfectiousOrderItem
): ICartInfectiousAliquot =>
  item.infectiousAliquot ?? item.infectious_aliquot ?? item.aliquot ?? (item as any);

const getNumber = (value: unknown, fallback = 0) => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : fallback;
};

const getInfectiousProductName = (aliquot: ICartInfectiousAliquot) => {
  const aliquotType = String(aliquot.aliquotType ?? "").trim().toLowerCase();

  if (aliquot.productId) {
    return aliquot.productId;
  }

  if (aliquotType === "aliquot") {
    return "Aliquot";
  }

  if (aliquotType === "bulk") {
    return "Bulk";
  }

  return "Infectious aliquot";
};

const normalizeInfectiousOrderItem = (
  item: ICartInfectiousOrderItem
): CartProduct => {
  const aliquot = getOrderItemAliquot(item);
  const numericAliquotId = getNumber(
    item.infectiousAliquotId ?? item.aliquotId ?? aliquot.id ?? item.id
  );
  const volume = getNumber(aliquot.volumeMl ?? aliquot.volume);
  const price = getNumber(item.price ?? aliquot.price);
  const specimenId =
    aliquot.specimenId ??
    aliquot.productId ??
    String(aliquot.aliquotId ?? item.aliquotId ?? numericAliquotId);
  const aliquotId = String(aliquot.aliquotId ?? item.aliquotId ?? numericAliquotId);
  const productName = getInfectiousProductName(aliquot);

  return {
    ...(item as any),
    ...(aliquot as any),
    id: numericAliquotId,
    cartItemType: "infectious",
    infectiousAliquotId: numericAliquotId,
    rawCartItem: item,
    biobankId: specimenId,
    accession: "",
    loincCode: aliquotId,
    loincBiomarker: {
      id: aliquotId,
      itemNumber: aliquotId,
      name: productName,
      displayName: productName,
      synonyms: productName,
      unitsUs: "mL",
    },
    volumeNumber: volume,
    volumeUnits: "mL",
    storageCondition: aliquot.storageCondition ?? "",
    storageContainer:
      aliquot.storageContainer ?? aliquot.container ?? aliquot.aliquotContainer ?? "",
    specimenPrice: price,
    collectionDate: "",
    expirationDate: "",
    status: "available",
    donorDob: "",
    donorGender: "",
    donorZip: "",
    donorEthnicity: "",
    invoiceId: 0,
    invoice: {} as any,
    biomarkerValue: volume,
    uom: "mL",
    matrix: aliquot.matrix ?? "Infectious",
    orderNumber: volume ? `${volume} mL` : "",
    freezerId: (aliquot as any).storageRefrigerator ?? "",
    rackId: (aliquot as any).storageRack ?? "",
    storageLocation: (aliquot as any).storageLocation ?? "",
  } as CartProduct;
};

export const getOrderProducts = (order?: ICart | null): CartProduct[] => {
  const samples = (order?.samples ?? []).map((sample) => ({
    ...sample,
    cartItemType: "sample" as const,
  }));
  const infectiousItems = getCartInfectiousOrderItems(order).map(
    normalizeInfectiousOrderItem
  );

  return [...samples, ...infectiousItems];
};

export const getCartProducts = getOrderProducts;

export const getCartItemsCount = (cart?: ICart | null) =>
  getCartProducts(cart).length;

export const hasSampleCartProducts = (products: Array<Partial<CartProduct>> = []) =>
  products.some((product) => product.cartItemType !== "infectious");

export const hasInfectiousCartProducts = (
  products: Array<Partial<CartProduct>> = []
) => products.some((product) => product.cartItemType === "infectious");

export const getCartTypeConflictTooltipProps = (isBlocked: boolean) =>
  isBlocked
    ? {
        "data-tooltip-id": "cart-type-conflict-tooltip",
        "data-tooltip-content": CART_TYPE_CONFLICT_TOOLTIP,
        "data-tooltip-place": "top" as const,
      }
    : {};
